import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchData, updateEmployee } from "../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArchive,
  faSearch,
  faUserSlash,
  faBoxes,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faAddressCard,
} from "@fortawesome/free-solid-svg-icons";
import "../main.scss";
import Can from "../permissions/Can";
import Tooltip from "../components/Tooltip";

const Archive = () => {
  const dispatch = useDispatch();
  const allTeachers = useSelector(
    (state) => state.main.data?.allteachers || []
  );
  const schoolsData = useSelector((state) => state.main.data?.schoolData || []);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await dispatch(fetchData());
      setLoading(false);
    };
    load();
  }, [dispatch]);

  // Filter employees who are not linked to any school or are in deleted schools
  const unlinkedEmployees = allTeachers.filter((employee) => {
    const rgmw = employee.rgmw;
    let isUnlinked = false;

    // Check if rgmw is null, undefined, empty string, or 0
    if (!rgmw || rgmw === "" || rgmw === 0) {
      isUnlinked = true;
    } else {
      // Check if the rgmw doesn't exist in active schools table
      const schoolExists = schoolsData.some((school) => school.rgmw === rgmw);
      if (!schoolExists) isUnlinked = true;
    }

    if (!isUnlinked) return false;

    // Apply search filter
    const searchLower = filter.toLowerCase();
    return (
      employee.emp_name?.toLowerCase().includes(searchLower) ||
      employee.emp_id?.toString().includes(searchLower)
    );
  });

  const handleArchiveSingle = async (employee) => {
    toast(
      (t) => (
        <div className="confirm-toast">
          <p style={{ fontWeight: 800, fontSize: "15px", marginBottom: "8px" }}>
            أرشفة موظف
          </p>
          <p>
            هل تريد نقل <b>{employee.emp_name}</b> إلى الأرشيف النهائي؟
          </p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const tid = toast.loading("جارٍ الأرشفة...");
                try {
                  await dispatch(
                    updateEmployee({
                      emp_id: employee.emp_id,
                      data: { rgmw: 11111111 },
                    })
                  ).unwrap();
                  toast.success("تم الأرشفة", { id: tid });
                  dispatch(fetchData());
                } catch {
                  toast.error("فشل الإجراء", { id: tid });
                } finally {
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
              style={{ backgroundColor: "var(--accent)" }}
            >
              نعم
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const handleMoveAllToArchive = async () => {
    if (unlinkedEmployees.length === 0) {
      toast.error("لا يوجد موظفين للنقل إلى الأرشيف");
      return;
    }

    toast(
      (t) => (
        <div className="confirm-toast">
          <p
            style={{ fontWeight: 800, fontSize: "16px", marginBottom: "10px" }}
          >
            تأكيد الأرشفة الجماعية
          </p>
          <p>
            هل أنت متأكد من نقل جميع الموظفين المظهرين (
            {unlinkedEmployees.length}) إلى الأرشيف النهائي؟
          </p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const loadingToast = toast.loading("جارٍ البدء في الأرشفة...");
                try {
                  const updatePromises = unlinkedEmployees.map((employee) =>
                    dispatch(
                      updateEmployee({
                        emp_id: employee.emp_id,
                        data: { rgmw: 11111111 },
                      })
                    ).unwrap()
                  );

                  await Promise.all(updatePromises);
                  toast.success("تم نقل جميع الموظفين بنجاح", {
                    id: loadingToast,
                  });
                  dispatch(fetchData());
                } catch (error) {
                  toast.error("فشلت العملية، يرجى المحاولة لاحقاً", {
                    id: loadingToast,
                  });
                } finally {
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
              style={{ backgroundColor: "var(--accent)" }}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> نعم، متأكد
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              <FontAwesomeIcon icon={faTimesCircle} /> تراجع
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        style: {
          background: "var(--panel)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          padding: "20px",
          borderRadius: "15px",
        },
      }
    );
  };

  return (
    <div className="archive-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        {/* Premium Compact Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faArchive} />
            </div>
            <div className="header-text-box">
              <h2>الأرشيف وسجلات الموظفين العالقة</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة الأرشيف</span>
              </div>
            </div>
          </div>

          <div className="header-tools">
            <div className="global-search-container">
              <FontAwesomeIcon icon={faSearch} className="s-icon" />
              <input
                type="text"
                placeholder="ابحث عن اسم الموظف أو رقم الهوية..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>

            <div className="results-count">
              <b>{unlinkedEmployees.length}</b> موظف
            </div>


            <Can module="employees" action="edit">
              <Tooltip content="أرشفة الجميع">
                <button
                  className="premium-action-btn archive-btn"
                  onClick={handleMoveAllToArchive}
                  style={{ background: "#4f46e5", color: "white" }}
               
                >
                  <FontAwesomeIcon icon={faBoxes} />
                  <span className="btn-text">أرشفة الجميع</span>
                </button>
              </Tooltip>
            </Can>
          </div>
        </div>

        <div className="employee-table-wrapper">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : (
            <table className="employee-table">
              <thead>
                <tr>
                  <th width="60">#</th>
                  <th>اسم الموظف</th>
                  <th>رقم الهوية</th>
                  <th width="150" style={{ textAlign: "center" }}>
                    الحالة
                  </th>
                  <th width="80" style={{ textAlign: "center" }}>
                    إجراء
                  </th>
                </tr>
              </thead>
              <tbody>
                {unlinkedEmployees.length > 0 ? (
                  unlinkedEmployees.map((employee, index) => (
                    <tr key={employee.emp_id}>
                      <td>{index + 1}</td>
                      <td style={{ fontWeight: 600 }}>
                        <div className="info-cell">
                          <div className="avatar-minimal">
                            <FontAwesomeIcon icon={faAddressCard} />
                          </div>
                          <span className="name">{employee.emp_name}</span>
                        </div>
                      </td>
                      <td>{employee.emp_id}</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="status-badge-lost">
                          تائه <FontAwesomeIcon icon={faExclamationTriangle} />
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="table-actions">
                          <Can module="employees" action="edit">
                            <Tooltip content="نقل للأرشيف">
                              <button
                                className="btn-table btn-table--edit"
                                onClick={() => handleArchiveSingle(employee)}
                             
                              >
                                <FontAwesomeIcon icon={faArchive} />
                              </button>
                            </Tooltip>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="empty-ui-state">
                      <FontAwesomeIcon icon={faUserSlash} className="icon" />
                      <h3>البيانات نظيفة</h3>
                      <p>
                        لا يوجد موظفين غير مرتبطين بمدارس حالياً، كل شيء يبدو
                        منظماً.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Card View */}
        <div className="mobile-card-view">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : unlinkedEmployees.length > 0 ? (
            unlinkedEmployees.map((employee, index) => (
              <div className="mobile-card" key={employee.emp_id}>
                <div className="mobile-card-header">
                  <span className="mobile-card-title">{employee.emp_name}</span>
                  <span className="status-badge-lost">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </span>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span>رقم الهوية:</span>
                    <span style={{ fontWeight: 600, color: "var(--text)" }}>
                      {employee.emp_id}
                    </span>
                  </div>
                  <div className="mobile-card-actions">
                    <Can module="employees" action="edit">
                      <button
                        className="premium-action-btn"
                        onClick={() => handleArchiveSingle(employee)}
                        style={{ padding: "8px 12px", fontSize: "12px" }}
                      >
                        <FontAwesomeIcon icon={faArchive} />
                      <span>نقل للأرشيف</span>
                    </button>
                    </Can>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-ui-state">
              <FontAwesomeIcon icon={faUserSlash} className="icon" />
              <h3>البيانات نظيفة</h3>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .archive-wrapper { padding: 5px; color: var(--text); font-family: 'Cairo', sans-serif; }
        .premium-edit-container { 
          margin: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
        }
        
        .premium-header-modern {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 30px; 
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          gap: 20px;
        }

        .premium-table-container { 
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
          padding: 30px 30px 40px !important;
          overflow-x: auto; 
        }

        /* Excel-like Table Styling */
        .employee-table-wrapper {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
          background: var(--panel);
          border-radius: 0 0 12px 12px;
          border: 1px solid var(--border);
          border-top: none;
          padding: 2px;
          box-sizing: border-box;
          margin-bottom: 20px; /* Add margin to ensure scrollbar is usable */
        }

        .employee-table {
          width: 100%;
          min-width: 600px; /* Force minimum width to enable scrolling */
          border-collapse: collapse;
          text-align: right;
          font-size: 13px;
          table-layout: auto;
        }

        .employee-table th {
          background: var(--panel-2);
          padding: 12px 10px;
          color: var(--accent);
          font-weight: 700;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }

        .employee-table td {
          padding: 10px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: var(--text);
          white-space: nowrap;
        }

        .employee-table tr:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .table-actions {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .btn-table {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--panel-2);
          cursor: pointer;
          transition: 0.2s;
          font-size: 14px;
        }

        .btn-table--edit { color: var(--accent); }
        .btn-table--edit:hover { 
          background: var(--accent); 
          color: white; 
          border-color: var(--accent); 
        }

        .header-icon-box {
          width: 48px; height: 48px; background: #f59e0b;
          border-radius: 14px; display: flex; align-items: center; justify-content: center;
          font-size: 20px; color: white;
          box-shadow: 0 8px 16px -4px rgba(245, 158, 11, 0.4);
        }

        .header-text-box h2 { margin: 0; font-size: clamp(18px, 2.2vw, 22px); font-weight: 800; }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; margin-top: 2px; }
        .breadcrumb-info span:first-child { color: #d97706; font-weight: 500; }
        .current-page { color: var(--accent); font-weight: 600; }
        .separator { margin: 0 4px; }

        .header-tools { display: flex; align-items: center; gap: 15px; flex: 1; justify-content: flex-end; }
        .global-search-container {
          background: var(--bg); border: 1px solid var(--border); border-radius: 12px;
          padding: 0 15px; display: flex; align-items: center; gap: 12px; width: 100%; max-width: 400px; height: 44px;
        }
        .global-search-container input { background: transparent; border: none; color: var(--text); font-size: 14px; width: 100%; outline: none; font-family: inherit; }
        .s-icon { color: #f59e0b; }
        .results-count {
          background: var(--panel-2);
          padding: 0 15px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--muted);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 6px;
          height: 44px;
          white-space: nowrap;
          transition: all 0.3s;
        }
        .results-count:hover { border-color: #f59e0b; background: rgba(245, 158, 11, 0.05); }
        .results-count b { color: #f59e0b; font-size: 15px; }

        .premium-action-btn {
          border: none; border-radius: 12px; padding: 10px 18px;
          display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 700;
          cursor: pointer; transition: 0.3s; font-family: 'Cairo', sans-serif !important;
          box-shadow: 0 8px 16px -4px rgba(245, 158, 11, 0.4);
        }
        .premium-action-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }

        /* Remove old table styles */
        .info-cell { display: flex; align-items: center; gap: 10px; }
        .avatar-minimal {
          width: 32px; height: 32px; background: var(--panel-2); border: 1px solid var(--border);
          border-radius: 8px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: #f59e0b;
        }

        .status-badge-lost {
          background: rgba(239, 68, 68, 0.1); color: #ef4444;
          padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700;
          display: inline-flex; align-items: center; gap: 6px;
        }

        .loader-overlay { padding: 60px; text-align: center; display: flex; flex-direction: column; gap: 20px; color: var(--muted); }
        .premium-loader { width: 40px; height: 40px; border: 3px solid rgba(245, 158, 11, 0.1); border-top-color: #f59e0b; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-ui-state { text-align: center; padding: 100px !important; color: var(--muted); }
        .empty-ui-state .icon { font-size: 60px; opacity: 0.15; margin-bottom: 25px; }
        .empty-ui-state h3 { font-size: 22px; font-weight: 800; color: var(--text); margin: 0; }
        .empty-ui-state p { margin-top: 15px; font-size: 15px; }

        .confirm-toast { text-align: center; font-family: 'Cairo', sans-serif; }
        .toast-actions { display: flex; justify-content: center; gap: 12px; margin-top: 20px; }
        .btn-confirm { border: none; padding: 10px 20px; border-radius: 12px; color: white; cursor: pointer; font-weight: 800; font-family: 'Cairo', sans-serif; }
        .btn-cancel { background: var(--panel-2); color: var(--text); border: 1px solid var(--border); padding: 10px 20px; border-radius: 12px; cursor: pointer; font-weight: 800; font-family: 'Cairo', sans-serif; }

        /* Mobile Card View */
          .mobile-card-view {
            display: none;
            flex-direction: column;
            gap: 15px;
            padding: 15px;
          }

          .mobile-card {
            background: var(--panel-2);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 15px;
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .mobile-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 12px;
            border-bottom: 1px solid var(--border);
          }

          .mobile-card-title {
            font-weight: 700;
            font-size: 15px;
            color: var(--text);
          }

          .mobile-card-body {
            display: flex;
            flex-direction: column;
            gap: 8px;
            font-size: 13px;
            color: var(--muted);
          }

          .mobile-card-row {
            display: flex;
            justify-content: space-between;
          }

          .mobile-card-actions {
            margin-top: 5px;
            display: flex;
            justify-content: flex-end;
          }

          @media (max-width: 900px) {
            .employee-table-wrapper { display: none; }
            .mobile-card-view { display: flex; }
            
            .premium-header-modern {
              padding: 14px 18px;
              gap: 10px;
              flex-wrap: nowrap;
              overflow-x: auto;
            }
            .premium-action-btn .btn-text { display: none; }
            .premium-action-btn { padding: 10px; }
            .avatar-minimal { display: none; }
            .header-left { gap: 10px; justify-content: flex-start; }
            .header-icon-box { width: 40px; height: 40px; font-size: 18px; }
            .header-text-box h2 { font-size: 18px; }
            .breadcrumb-info { font-size: 11px; }
            .header-tools { flex-direction: row; gap: 8px; width: auto; }
            .global-search-container, .results-count { display: none; }
          }

        @media (max-width: 480px) {
          .premium-header-modern { padding: 12px 10px; }
        }
      `}</style>
    </div>
  );
};

export default Archive;
